import { createContext, useMemo } from 'react'
import { PermissionAction, PermissionCheck, PermissionResource, UserPermissions } from '@/types/permissions.types'

interface IPermissionProvider {
  children: React.ReactNode
}

type PermissionContextData = {
  PERMISSIONS: UserPermissions | null
  GET_RESOURCE_PERMISSIONS: (resource: PermissionResource) => PermissionAction[]
  HAS_PERMISSION: (resource: PermissionResource, action?: PermissionAction) => boolean
  HAS_ALL_PERMISSIONS: (permissions: PermissionCheck[]) => boolean
  HAS_RESOURCE_PERMISSION: (resource: PermissionResource) => boolean
}

export const PermissionContext = createContext({} as PermissionContextData)

export function PermissionProvider({ children }: IPermissionProvider) {
  const userToken = localStorage.getItem('avalia-token')

  const DESERIALIZE_TOKEN = (token: string | null | undefined): UserPermissions | null => {
    if (!token || typeof token !== 'string') return null
    
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]))
      const { role } = tokenPayload
      if (!role) return null

      const resources: { [key: string]: PermissionAction[] } = {}
      const rolePrefix = `${role}_`
      Object.keys(tokenPayload).forEach((key) => {
        if (
          key.startsWith(rolePrefix) &&
          (Array.isArray(tokenPayload[key]) || typeof tokenPayload[key] === 'string')
        ) {
          resources[key] = tokenPayload[key] as PermissionAction[]
        }
      })

      return {
        role,
        resources,
      }
    } catch (error) {
      console.error('Erro ao decodificar token:', error)
      return null
    }
  }

  const PERMISSIONS = useMemo<UserPermissions | null>(() => {
    return DESERIALIZE_TOKEN(userToken as string)
  }, [userToken])

  const permissionCache = useMemo(() => new Map<string, boolean>(), [PERMISSIONS])

  const GET_PERMISSION_KEY = (resource: string): string => {
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)
    return `${PERMISSIONS?.role}_${capitalize(resource)}`
  }

  const HAS_PERMISSION = (
    resource: PermissionResource,
    action: PermissionAction = PermissionAction.READ,
  ): boolean => {
    if (!PERMISSIONS?.resources) return false

    const cacheKey = `${resource}_${action}`

    if (permissionCache.has(cacheKey)) {
      return permissionCache.get(cacheKey)!
    }

    const permissionKey = GET_PERMISSION_KEY(resource)
    const resourcePermissions = PERMISSIONS.resources[permissionKey]
    const hasPermission = Array.isArray(resourcePermissions)
      ? resourcePermissions.includes(action)
      : resourcePermissions === action

    permissionCache.set(cacheKey, hasPermission)

    return hasPermission
  }

  const HAS_ALL_PERMISSIONS = (permissions: PermissionCheck[]): boolean => {
    return permissions.every(({ resource, action }) => HAS_PERMISSION(resource, action))
  }

  const HAS_RESOURCE_PERMISSION = (resource: PermissionResource): boolean => {
    if (!PERMISSIONS?.resources) return false

    const permissionKey = GET_PERMISSION_KEY(resource)
    const resourcePermissions = PERMISSIONS.resources[permissionKey]

    return Array.isArray(resourcePermissions) ? resourcePermissions.length > 0 : !!resourcePermissions
  }

  const GET_RESOURCE_PERMISSIONS = (resource: PermissionResource): PermissionAction[] => {
    if (!PERMISSIONS?.resources) return []

    const permissionKey = GET_PERMISSION_KEY(resource)
    const resourcePermissions = PERMISSIONS.resources[permissionKey]

    return resourcePermissions ?? []
  }

  return (
    <PermissionContext.Provider
      value={{
        PERMISSIONS,
        GET_RESOURCE_PERMISSIONS,
        HAS_PERMISSION,
        HAS_ALL_PERMISSIONS,
        HAS_RESOURCE_PERMISSION
      }}
    >
      {children}
    </PermissionContext.Provider>
  )
}
